import { computed, shallowRef, watch, type ComputedRef, type Ref } from "vue";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  nothing,
  matchMaybe,
} from "../lib/maybe";
import { createTimeoutHandle } from "../lib/timeoutHandle";
import {
  collectEntityPeekIndex,
  entityPeekScopeAllows,
  type EntityPeekEntry,
} from "../editor/entityPeekIndex";
import type { ProsePeekContext } from "../editor/prose/entityPeek";
import { scenePath, scenePathsForChapters } from "../lib/zwConfig";
import { displayModeIsWriting, type DisplayMode } from "../lib/displayMode";
import { useActiveEntityFieldsStore } from "../stores/useActiveEntityFieldsStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useOutlineEntitiesStore } from "../stores/useOutlineEntitiesStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";

/**
 * Entity underlines + hover peek (#73, ProseMirror since #250): scenes
 * only, live writing only. Keeps the Database name index fresh (debounced)
 * and hands the editor `index` ([] wherever peek is off) plus the card's
 * shell context.
 */
export function useEntityPeek(options: {
  displayMode: Ref<DisplayMode>;
  reviewModeActive: Ref<boolean>;
}): {
  /** Entries to underline: the index while peek is allowed, else []. */
  index: ComputedRef<readonly EntityPeekEntry[]>;
  peekContext: ProsePeekContext;
  scheduleIndexRefresh: () => void;
  cleanup: () => void;
} {
  const session = useEditorSessionStore();
  const outlineEntities = useOutlineEntitiesStore();
  const activeEntityFields = useActiveEntityFieldsStore();
  const workspace = useProjectWorkspaceStore();

  const indexTimer = createTimeoutHandle();
  let indexToken = 0;
  const entries = shallowRef<readonly EntityPeekEntry[]>([]);

  const allowed = computed(
    () =>
      displayModeIsWriting(options.displayMode.value) &&
      options.reviewModeActive.value === false &&
      entityPeekScopeAllows("scenes", {
        activeSceneInfo: session.activeSceneInfo,
        editingSidecar: session.editingSidecar,
      }),
  );

  const isAllowed = (): boolean => allowed.value;

  const peekContext: ProsePeekContext = {
    rootPath: () => fromMaybe(session.rootPath),
    manuscript: () =>
      matchMaybe(session.activeSceneInfo, {
        nothing: () => null,
        just: (info) => ({
          currentScenePath: scenePath(info.folder, info.sceneFile),
          sceneOrder: scenePathsForChapters(session.chapters),
        }),
      }),
    openEntry: (fileName) => {
      void workspace.onSelectProjectDatabase(fileName);
    },
  };

  function scheduleIndexRefresh(): void {
    indexTimer.schedule(200, () => {
      void refreshIndex();
    });
  }

  async function refreshIndex(): Promise<void> {
    if (isNothing(session.rootPath) || allowed.value === false) {
      entries.value = [];
      return;
    }
    const root = session.rootPath.value;
    const token = ++indexToken;
    const activeDbM = session.activeDatabase;
    const live =
      isJust(activeDbM) && session.activeMap === false
        ? (() => {
            const activeDb = activeDbM.value;
            const entryFields = activeEntityFields.fieldsForEntry(
              root,
              activeDb,
            );
            return just({
              fileName: activeDb,
              doc: session.doc,
              fields: entryFields,
            });
          })()
        : nothing;
    try {
      const next = await collectEntityPeekIndex(root, live);
      // Stale (a newer refresh started) or peek turned off while loading.
      if (token !== indexToken || isAllowed() === false) return;
      entries.value = next;
    } catch (error) {
      console.error("Failed to build entity peek index", error);
    }
  }

  watch(allowed, () => scheduleIndexRefresh(), { immediate: true });

  watch(
    () => [
      fromMaybe(session.rootPath),
      outlineEntities.databaseEntryFields,
      session.activeDatabase,
      isJust(session.activeDatabase) ? session.doc : null,
      activeEntityFields.fields.plural,
      activeEntityFields.fields.adjective,
      activeEntityFields.fields.aka,
      activeEntityFields.fields.arc,
      activeEntityFields.fields.type,
    ],
    () => scheduleIndexRefresh(),
  );

  return {
    index: computed(() => (allowed.value ? entries.value : [])),
    peekContext,
    scheduleIndexRefresh,
    cleanup: () => indexTimer.clear(),
  };
}
